import { useState, useEffect } from 'react';

type ViewMode = 'grid' | 'table';

/**
 * Custom hook to manage view preference (grid/table) with localStorage persistence
 * @param pageKey - Unique identifier for the page (e.g., 'features', 'plans', 'organisations')
 * @param defaultMode - Default view mode if no preference is stored
 * @returns [viewMode, setViewMode] tuple
 */
export function useViewPreference(
  pageKey: string,
  defaultMode: ViewMode = 'grid'
): [ViewMode, (mode: ViewMode) => void] {
  const storageKey = `viewMode_${pageKey}`;

  // Initialize state from localStorage or default
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return (stored as ViewMode) || defaultMode;
    } catch (error) {
      console.warn(`Failed to read view preference for ${pageKey}:`, error);
      return defaultMode;
    }
  });

  // Update localStorage whenever viewMode changes
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, viewMode);
    } catch (error) {
      console.warn(`Failed to save view preference for ${pageKey}:`, error);
    }
  }, [storageKey, viewMode, pageKey]);

  // Wrapper function to update both state and localStorage
  const setViewMode = (mode: ViewMode) => {
    setViewModeState(mode);
  };

  return [viewMode, setViewMode];
}
